import { motion } from 'framer-motion';

interface AnimatedLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'light' | 'dark';
  className?: string;
}

export function AnimatedLogo({ 
  size = 'md',
  variant = 'default',
  className = ''
}: AnimatedLogoProps) {
  const sizeClasses = {
    sm: 'h-16',
    md: 'h-20',
    lg: 'h-28',
    xl: 'h-36'
  };

  const getGradientId = (variant: string) => `people-gradient-animated-${variant}`;
  const getStarGradientId = (variant: string) => `star-gradient-animated-${variant}`;

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        viewBox="0 0 400 120"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Better to Give than Receive - Give Better Gifts"
      >
        <defs>
          <linearGradient id={getGradientId(variant)} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="35%" stopColor="#c41e3a" />
            <stop offset="65%" stopColor="#5a7139" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
          
          <linearGradient id={getStarGradientId(variant)} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="50%" stopColor="#c41e3a" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
        </defs>

        <motion.g 
          transform="translate(35, 60)"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* 8 people arranged in a circle */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <motion.g 
              key={i} 
              transform={`rotate(${angle}, 0, 0)`}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: i * 0.1,
                duration: 0.4,
                ease: "easeOut"
              }}
            >
              <circle cx="0" cy="-30" r="3.5" fill={`url(#${getGradientId(variant)})`} />
              <path d="M 0,-26.5 L -4,-18 L 0,-20 L 4,-18 L 0,-26.5 Z" fill={`url(#${getGradientId(variant)})`} />
              <path d="M 0,-18 L -2,-10" stroke={`url(#${getGradientId(variant)})`} strokeWidth="2.5" fill="none" />
              <path d="M 0,-18 L 2,-10" stroke={`url(#${getGradientId(variant)})`} strokeWidth="2.5" fill="none" />
            </motion.g>
          ))}
          
          {/* Central star - appears last */}
          <motion.g
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              delay: 0.9,
              duration: 0.5,
              ease: "easeOut"
            }}
          >
            <path d="M 0,-10 L -2,-5 L -7,-7 L -4,-3 L -8,0 L -4,3 L -7,7 L -2,5 L 0,10 L 2,5 L 7,7 L 4,3 L 8,0 L 4,-3 L 7,-7 L 2,-5 Z" 
                  fill={`url(#${getStarGradientId(variant)})`} />
            <circle cx="0" cy="0" r="4" fill={`url(#${getStarGradientId(variant)})`} opacity="0.6" />
          </motion.g>
        </motion.g>

        {/* Text - fades in after icon */}
        <motion.g
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          <text
            x="80"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="18"
            fontWeight="700"
            letterSpacing="1.5"
            fill={variant === 'light' ? '#ffffff' : '#2d3319'}
          >
            BETTER
          </text>
          <text
            x="152"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="11"
            fontWeight="600"
            letterSpacing="0.5"
            fill={variant === 'light' ? '#ffffff' : '#2d3319'}
            opacity="0.6"
          >
            TO
          </text>
          <text
            x="175"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="18"
            fontWeight="700"
            letterSpacing="1.5"
            fill="#2d5016"
          >
            GIVE
          </text>
          <text
            x="230"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="11"
            fontWeight="600"
            letterSpacing="0.5"
            fill={variant === 'light' ? '#ffffff' : '#2d3319'}
            opacity="0.6"
          >
            THAN
          </text>
          <text
            x="275"
            y="50"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="18"
            fontWeight="700"
            letterSpacing="1.5"
            fill="#c41e3a"
          >
            RECEIVE
          </text>

          <circle cx="147" cy="47" r="1.5" fill="#f4a460" />
          <circle cx="225" cy="47" r="1.5" fill="#f4a460" />

          <text
            x="80"
            y="75"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14"
            fontWeight="600"
            letterSpacing="3"
            fill="#f4a460"
          >
            GIVE
          </text>
          <text
            x="128"
            y="75"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14"
            fontWeight="600"
            letterSpacing="3"
            fill="#2d5016"
          >
            BETTER
          </text>
          <text
            x="212"
            y="75"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontSize="14"
            fontWeight="600"
            letterSpacing="3"
            fill="#c41e3a"
          >
            GIFTS
          </text>

          <line
            x1="80"
            y1="80"
            x2="265"
            y2="80"
            stroke="#f4a460"
            strokeWidth="1"
            opacity="0.4"
          />
        </motion.g>
      </svg>
    </div>
  );
}

// Animated icon with gentle pulse
export function AnimatedLogoIcon({ 
  size = 'md',
  className = '' 
}: { 
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16'
  };

  const getGradientId = (size: string) => `people-gradient-icon-animated-${size}`;
  const getStarGradientId = (size: string) => `star-gradient-icon-animated-${size}`;

  return (
    <motion.div 
      className={`flex items-center justify-center ${className}`}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.2 }}
    >
      <svg
        viewBox="0 0 80 80"
        className={sizeClasses[size]}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="BtGtR"
      >
        <defs>
          <linearGradient id={getGradientId(size)} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="35%" stopColor="#c41e3a" />
            <stop offset="65%" stopColor="#5a7139" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
          
          <linearGradient id={getStarGradientId(size)} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="50%" stopColor="#c41e3a" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
        </defs>

        <g transform="translate(40, 40)">
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <g key={i} transform={`rotate(${angle}, 0, 0)`}>
              <circle cx="0" cy="-28" r="4" fill={`url(#${getGradientId(size)})`} />
              <path d="M 0,-24 L -4.5,-14 L 0,-16 L 4.5,-14 L 0,-24 Z" fill={`url(#${getGradientId(size)})`} />
              <path d="M 0,-14 L -2,-6" stroke={`url(#${getGradientId(size)})`} strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M 0,-14 L 2,-6" stroke={`url(#${getGradientId(size)})`} strokeWidth="3" fill="none" strokeLinecap="round" />
            </g>
          ))}
          
          {/* Gently pulsing star */}
          <motion.g
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.8, 1, 0.8]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <path d="M 0,-12 L -2,-6 L -8,-8 L -5,-3 L -10,0 L -5,3 L -8,8 L -2,6 L 0,12 L 2,6 L 8,8 L 5,3 L 10,0 L 5,-3 L 8,-8 L 2,-6 Z" 
                  fill={`url(#${getStarGradientId(size)})`} />
            <circle cx="0" cy="0" r="5" fill={`url(#${getStarGradientId(size)})`} opacity="0.6" />
          </motion.g>
        </g>
      </svg>
    </motion.div>
  );
}

// Loading animation - people appear one by one
export function LoadingLogo() {
  const getGradientId = () => 'people-gradient-loading';
  const getStarGradientId = () => 'star-gradient-loading';

  return (
    <div className="flex items-center justify-center">
      <svg
        viewBox="0 0 80 80"
        className="h-16 w-16"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={getGradientId()} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="35%" stopColor="#c41e3a" />
            <stop offset="65%" stopColor="#5a7139" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
          
          <linearGradient id={getStarGradientId()} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f4a460" />
            <stop offset="50%" stopColor="#c41e3a" />
            <stop offset="100%" stopColor="#2d5016" />
          </linearGradient>
        </defs>

        <motion.g 
          transform="translate(40, 40)"
          animate={{ rotate: 360 }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <motion.g 
              key={i} 
              transform={`rotate(${angle}, 0, 0)`}
              initial={{ opacity: 0.2 }}
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.125,
                ease: "easeInOut"
              }}
            >
              <circle cx="0" cy="-28" r="4" fill={`url(#${getGradientId()})`} />
              <path d="M 0,-24 L -4.5,-14 L 0,-16 L 4.5,-14 L 0,-24 Z" fill={`url(#${getGradientId()})`} />
              <path d="M 0,-14 L -2,-6" stroke={`url(#${getGradientId()})`} strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M 0,-14 L 2,-6" stroke={`url(#${getGradientId()})`} strokeWidth="3" fill="none" strokeLinecap="round" />
            </motion.g>
          ))}
          
          <motion.g
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <path d="M 0,-12 L -2,-6 L -8,-8 L -5,-3 L -10,0 L -5,3 L -8,8 L -2,6 L 0,12 L 2,6 L 8,8 L 5,3 L 10,0 L 5,-3 L 8,-8 L 2,-6 Z" 
                  fill={`url(#${getStarGradientId()})`} />
          </motion.g>
        </motion.g>
      </svg>
    </div>
  );
}
